import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { HttpService } from '@app/services/http.service';
import { PasswordStrengthValidator, ConfirmPasswordValidator, SelectedRoleValidator } from '../../../library/validators.class';
import { environment } from '@env/environment';
import { Role } from '../../../models/role';
import { ActivatedRoute, Router } from '@angular/router';
import { Person } from '@app/models/person';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { forkJoin, Observable } from 'rxjs';
import { AppComponent } from '@app/app.component';
import { PageTitleService } from '@app/services/page-title.service';
import { LoaderService } from '@app/services/loader.service';
import { MatDialog } from '@angular/material/dialog';
import { PasswordChangeDialogComponent } from '../password-change-dialog/password-change-dialog.component';
import { BaseComponent } from '@app/base.component';
import { SnackbarService } from '@app/services/snackbar.service';
import { of, finalize } from 'rxjs';

interface UserFormValue {
  first_name: string;
  last_name: string;
  preferred_name: string | null;
  phone: string;
  username: string;
  email: string;
  password?: string;
  confirm_password?: string;
  has_password_change: boolean;
  title: string | null;
  roles: number[];
}

@Component({
  selector: 'app-user-edit',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.css']
})
export class UserEditComponent extends BaseComponent implements OnInit {

  // State management
  isLoaded = false;
  isRegister = true;
  isUserFormSubmitting = false;
  isRoleFormSubmitting = false;
  isPasswordFormSubmitting = false;
  
  // Data
  activeRoles: Role[] = [];
  personId: number | null = null;
  
  // UI
  subTitle: string = 'Register a new User';
  hidePassword: boolean = true;
  urlBack: string = '/administration/users';

  // Form
  userForm!: FormGroup;
  roleForm!: FormGroup;
  passwordForm!: FormGroup;

  // Table columns for role display
  displayedRoleColumns: string[] = ['role_name', 'select'];

  // patient form
  // personForm: UntypedFormGroup = new UntypedFormGroup({});
  // roleForm: UntypedFormGroup = new UntypedFormGroup({});

  constructor(
    private formBuilder: FormBuilder,
    private httpService: HttpService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private appComponent: AppComponent,
    private pageTitleService: PageTitleService,
    private loaderService: LoaderService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService, 
  ) {
    super();
  }

  ngOnInit(): void {
    this.pageTitleService.setPageTitle('Users Administration');
    this.appComponent.appendSiteTitle('administration', 'users', 'edit')

    this.initializeComponent();
  }

  private initializeComponent(): void {
    this.activatedRoute.params
      .pipe(
        takeUntil(this.appUnsubscribe),
        switchMap(params => {
          this.personId = params['person_id'] ? +params['person_id'] : null;
          this.isRegister = !this.personId;
          this.subTitle = this.isRegister ? 'Register a new User' : 'Edit User';
          
          return this.loadInitialData();
        }),
        catchError(error => {
          console.error('Error initializing component:', error);
          this.snackbarService.show('Error loading user data. Please try again.');
          this.router.navigate([this.urlBack]);
          return of(null);
        })
      )
      .subscribe();
  }

  private loadInitialData(): Observable<{ roles: Role[], person?: Person }> {
    const roles$ = this.fetchRoles();
    
    if (this.isRegister) {
      return roles$.pipe(
        switchMap(roles => {
          this.activeRoles = roles;
          this.buildForms();
          this.isLoaded = true;
          return of({ roles });
        })
      );
    }
    
    // Edit mode - load both roles and person data
    const person$ = this.fetchPerson(this.personId!);
    
    return forkJoin({ roles: roles$, person: person$ }).pipe(
      switchMap(({ roles, person }) => {
        this.activeRoles = roles;
        this.buildForms();
        this.populateForms(person);
        this.isLoaded = true;
        return of({ roles, person });
      })
    );
  }

  private buildForms(): void {
    // User Information Form
    const userFormConfig: any = {
      first_name: [null, Validators.required],
      last_name: [null, Validators.required],
      preferred_name: [null],
      phone: [null, [
        Validators.required,
        Validators.pattern(/^(\+?\d{1,4}[\s-])?(?!0+\s+,?$)\d{10}\s*,?$/)
      ]],
      username: [null, Validators.required],
      email: [null, [Validators.required, Validators.email]],
      has_password_change: [true],
      is_active: [true],
      title: [null]
    };

    // Password fields - required for registration, optional for edit
    if (this.isRegister) {
      // Registration: password is required
      userFormConfig.password = [null, [Validators.required, PasswordStrengthValidator]];
      userFormConfig.confirm_password = [null, Validators.required];
    } else {
      // Edit mode: password is optional
      userFormConfig.password = [null, [PasswordStrengthValidator]];
      userFormConfig.confirm_password = [null];
    }

    this.userForm = this.formBuilder.group(userFormConfig);

    // Set up password confirmation validator
    this.userForm.get('confirm_password')?.setValidators([
      (control) => ConfirmPasswordValidator(control, this.userForm)
    ]);

    // In edit mode, make confirm_password required if password has a value
    if (!this.isRegister) {
      this.userForm.get('password')?.valueChanges
        .pipe(takeUntil(this.appUnsubscribe))
        .subscribe(passwordValue => {
          const confirmPasswordControl = this.userForm.get('confirm_password');
          if (passwordValue && passwordValue.length > 0) {
            // Password has value, make confirm required
            confirmPasswordControl?.setValidators([
              Validators.required,
              (control) => ConfirmPasswordValidator(control, this.userForm)
            ]);
          } else {
            // Password is empty, confirm is optional
            confirmPasswordControl?.setValidators([
              (control) => ConfirmPasswordValidator(control, this.userForm)
            ]);
          }
          confirmPasswordControl?.updateValueAndValidity({ emitEvent: false });
        });
    }

    // Role Form
    this.roleForm = this.formBuilder.group({
      roles: [[], SelectedRoleValidator]
    });
  }

  // Populate forms with existing person data
  private populateForms(person: Person): void {
    if (!person) {
      return;
    }

    // Populate user info form
    this.userForm.patchValue({
      first_name: person.first_name,
      last_name: person.last_name,
      preferred_name: person.preferred_name,
      title: person.physician?.title || null,
      phone: person.phone,
      username: person.user?.username,
      email: person.user?.email,
      has_password_change: person.user?.has_password_change ?? true,
      is_active: person.user.is_active ?? true
    });

    // Populate role form
    const roleValues = this.activeRoles
      .filter(role => person.user?.roles?.some(r => r.role_id === role.role_id))
      .map(role => role.role_id);

    this.roleForm.patchValue({
      roles: roleValues
    });
  }

  private fetchRoles(): Observable<Role[]> {
    const url = this.httpService.createUrl(
      ['api', 'roles'],
      {},
      { host: environment.host_api_default }
    );
    
    return this.httpService.get<Role[]>(url).pipe(
      catchError(error => {
        console.error('Error fetching roles:', error);
        this.snackbarService.show('Error loading roles');
        return of([]);
      })
    );
  }

  private fetchPerson(personId: number): Observable<Person> {
    const url = this.httpService.createUrl(
      ['api', 'users', personId],
      {},
      { host: environment.host_api_default }
    );
    
    return this.httpService.get<Person>(url).pipe(
      catchError(error => {
        console.error('Error fetching person:', error);
        this.snackbarService.show('Error loading user data');
        throw error;
      })
    );
  }

  handleBack(): void {
    this.router.navigate([this.urlBack]);
  }

  // UI State Methods
  isUserFormLoaded(): boolean {
    return this.isLoaded;
  }

  isUserFormValid(): boolean {
    return this.userForm.valid && !this.isUserFormSubmitting;
  }

  isRoleFormValid(): boolean {
    return this.roleForm.valid && !this.isRoleFormSubmitting;
  }

  // Check if a role is currently selected
  isRoleSelected(roleId: number): boolean {
    const selectedRoles = this.roleForm.get('roles')?.value || [];
    return selectedRoles.includes(roleId);
  }

  // Toggle role selection
  toggleRole(roleId: number, isChecked: boolean): void {
    const rolesControl = this.roleForm.get('roles');
    if (!rolesControl) {
      return;
    }

    let selectedRoles = rolesControl.value || [];

    if (isChecked) {
      // Add role if not already present
      if (!selectedRoles.includes(roleId)) {
        selectedRoles = [...selectedRoles, roleId];
      }
    } else {
      // Remove role
      selectedRoles = selectedRoles.filter((id: number) => id !== roleId);
    }

    rolesControl.setValue(selectedRoles);
    rolesControl.markAsDirty();
    rolesControl.updateValueAndValidity();
  } 

  // User Information Form Submit
  handleUserFormSubmit(): void {
    this.userForm.markAllAsTouched();

    if (!this.userForm.valid || this.isUserFormSubmitting) {
      return;
    }

    this.isUserFormSubmitting = true;
    this.loaderService.show();

    const operation$ = this.isRegister 
      ? this.createUser() 
      : this.updateUser();

    operation$
      .pipe(
        takeUntil(this.appUnsubscribe),
        finalize(() => {
          this.isUserFormSubmitting = false;
          this.loaderService.hide();
        }),
        catchError(error => {
          console.error('Error saving user info:', error);
          this.snackbarService.show('Error saving user information. Please try again.');
          return of(null);
        })
      )
      .subscribe(person => {
        if (person) {
          if (this.isRegister) {
            this.snackbarService.show('User created successfully. You can now assign roles.');
            // Navigate to edit page so they can assign roles
            this.router.navigate(['/administration/users', person.person_id, 'edit']);
          } else {
            this.snackbarService.show('User information saved successfully');
            // Clear password fields after successful update
            this.userForm.patchValue({
              password: null,
              confirm_password: null
            });
          }
        }
      });
  }

  // Create new user
  private createUser(): Observable<any> {
    // For new user, submit user info with password
    const userData = {
      ...this.userForm.value
    };

    const url = this.httpService.createUrl(
      ['admin', 'register'],
      {},
      { host: environment.host_api_default }
    );
    
    return this.httpService.post<any>(url, userData);
  }

  // Update existing user
  private updateUser(): Observable<any> {
    const url = this.httpService.createUrl(
      ['admin', 'users', this.personId, 'update'],
      {},
      { host: environment.host_api_default }
    );
    
    // Prepare data - only include password if it was provided
    const updateData = { ...this.userForm.value };
    
    // Remove password fields if they're empty (optional update)
    if (!updateData.password || updateData.password.trim() === '') {
      delete updateData.password;
      delete updateData.confirm_password;
    }
    
    return this.httpService.put(url, updateData);
  }

  // Role Form Submit
  handleRoleFormSubmit(): void {
    this.roleForm.markAllAsTouched();
    
    const rolesControl = this.roleForm.get('roles');
    if (rolesControl) {
      rolesControl.markAsDirty();
      rolesControl.updateValueAndValidity();
    }

    if (!this.roleForm.valid || this.isRoleFormSubmitting) {
      return;
    }

    this.isRoleFormSubmitting = true;
    this.loaderService.show();

    this.updateRoles()
      .pipe(
        takeUntil(this.appUnsubscribe),
        finalize(() => {
          this.isRoleFormSubmitting = false;
          this.loaderService.hide();
        }),
        catchError(error => {
          console.error('Error saving roles:', error);
          this.snackbarService.show('Error saving user roles. Please try again.');
          return of(null);
        })
      )
      .subscribe(result => {
        if (result) {
          this.snackbarService.show('User roles updated successfully');
        }
      });
  }

  private updateRoles(): Observable<any> {
    const url = this.httpService.createUrl(
      ['admin', 'users', this.personId, 'roles'],
      {},
      { host: environment.host_api_default }
    );
    
    return this.httpService.put(url, this.roleForm.value);
  }


  // Password Form Submit (for registration only)
  handlePasswordSubmit(): void {
    if (!this.passwordForm) {
      return;
    }

    this.passwordForm.markAllAsTouched();

    if (!this.passwordForm.valid || this.isPasswordFormSubmitting) {
      return;
    }

    // Password is submitted with user info during registration
    this.snackbarService.show('Password will be set when you save user information');
  }

  // Password Change Dialog (for editing existing users)
  changePassword(): void {
    if (!this.personId) {
      return;
    }

    const dialogRef = this.dialog.open(PasswordChangeDialogComponent, {
      width: '360px',
      data: {
        user_id: this.personId,
        required: false,
        is_current_user: false
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.appUnsubscribe))
      .subscribe(result => {
        if (result) {
          this.snackbarService.show(`Password ${result}`);
        }
      });
  }


}
